import type { StoreApi } from "zustand/vanilla";
import type { CatalogCollectionType } from "../domain/types";
import type { PlayerRepeatMode, PlayerStore } from "./types";
import {
  selectActiveQueue,
  selectActiveQueueType,
  selectActiveSong,
  selectActiveSongId,
  selectActiveQueueItemId,
  selectActiveCollectionType,
  selectActiveCollectionId,
  selectActiveCollectionName,
  selectPlaybackStatus,
  selectIsPlaying,
  selectIsBuffering,
  // selectProgress removed - progress now in shared values
  // selectBufferedPosition removed — buffered now in shared values
  selectRepeatMode,
  selectShuffleOn,
  selectCanSkip,
  selectCanSkipReason,
  selectIsQueueEmpty,
  selectIsQueueLoading,
  selectIsLoadingMore,
  selectIsAutoPlayEnabled,
} from "./selectors";
import type { createPlayerBreadcrumbs } from "./createPlayerBreadcrumbs";

/**
 * Standalone actions for the player that can be used outside of React hooks/components.
 * These use getState() internally and are not reactive.
 */
export const createPlayerActions = (
  store: StoreApi<PlayerStore>,
  addPlayerBreadcrumb: ReturnType<
    typeof createPlayerBreadcrumbs
  >["addPlayerBreadcrumb"],
) => ({
  // Direct access to all raw actions for convenience
  ...store.getState().actions,

  // Flattened/Helper actions matching PlayerActions interface
  play: async () => await store.getState().actions.play(),
  pause: async () => await store.getState().actions.pause(),
  togglePlay: () => {
    const state = store.getState();
    if (selectIsPlaying(state)) {
      state.actions.pause().catch(console.error);
    } else {
      state.actions.play().catch(console.error);
    }
  },
  seekTo: async (time: number) => await store.getState().actions.seek(time),
  startSeek: () => {
    addPlayerBreadcrumb("progress.seek.start", store.getState());
  },
  seekPreview: () => {},
  endSeek: async (time: number) => {
    const state = store.getState();
    addPlayerBreadcrumb("progress.seek.end", state, {
      requestedPositionSeconds: time,
      requestedPositionMs: time * 1000,
    });
    await state.actions.seek(time);
  },
  next: () => store.getState().actions.next(),
  previous: () => store.getState().actions.prev(),
  jumpTo: (qid: string, options?: { autoPlay?: boolean }) =>
    store.getState().actions.jumpTo(qid, options),
  playFromQueueItem: (qid: string, options?: { autoPlay?: boolean }) => {
    const { actions } = store.getState();
    // jumpTo is responsible for applying userIntent + starting playback (if appropriate).
    actions.jumpTo(qid, { autoPlay: options?.autoPlay });
  },
  playFromCollection: (args: {
    songId: string;
    collectionType: CatalogCollectionType;
    collectionId: string;
    collectionName?: string;
    songs?: import("../domain/types").CatalogSong[];
    options?: { autoPlay?: boolean };
  }) => {
    const { actions } = store.getState();
    actions
      .playCollection(
        args.collectionId,
        args.collectionType,
        args.songId,
        args.songs,
        args.collectionName,
      )
      .catch(console.error);
  },
  toggleShuffle: () => store.getState().actions.toggleShuffle(),
  setShuffleOn: (on: boolean) => {
    const { actions, activeQueueType, playlistQueue } = store.getState();
    if (activeQueueType === "playlist") {
      actions.setGlobalShuffleEnabled(on);
    }
    if (
      activeQueueType === "playlist" &&
      playlistQueue?.shuffleState.isOn !== on
    ) {
      actions.toggleShuffle();
    }
  },
  setRepeatMode: (mode: PlayerRepeatMode) =>
    store.getState().actions.setRepeatMode(mode),
  cycleRepeatMode: () => {
    const { repeatMode, actions } = store.getState();
    const nextMode =
      repeatMode === "off" ? "queue" : repeatMode === "queue" ? "track" : "off";
    actions.setRepeatMode(nextMode);
  },
  setAutoPlayEnabled: (enabled: boolean) =>
    store.getState().actions.setAutoPlayEnabled(enabled),
  setActiveQueueType: (type: "foryou" | "playlist") => {
    const { actions } = store.getState();
    const state = getPlayerState(store);
    if (type === "foryou") {
      if (state.foryouQueue && state.foryouQueue.items.length > 0) {
        // Resume existing foryou queue
        actions.setActiveQueueType("foryou");
      } else {
        // ForYou queue is empty — just switch the view and pause
        // If ForYou bootstrap is enabled, it will load the queue independently
        actions.setActiveQueueType("foryou");
        actions.pause().catch(console.error);
      }
    } else {
      actions.setActiveQueueType("playlist");
    }
  },
  addNext: (songId: string) => store.getState().actions.addNext(songId),
  removeFromQueue: (queueItemIds: string[]) =>
    store.getState().actions.removeFromQueue(queueItemIds),
  setUpcomingQueueItemIds: (queueItemIds: string[]) =>
    store.getState().actions.setUpcomingQueueItemIds(queueItemIds),
});

/**
 * Non-reactive way to get the current player state.
 * Useful for logic outside of React components.
 */
export const getPlayerState = (store: StoreApi<PlayerStore>) => {
  const s = store.getState();
  return {
    activeQueue: selectActiveQueue(s),
    activeQueueType: selectActiveQueueType(s),
    activeSong: selectActiveSong(s),
    activeSongId: selectActiveSongId(s),
    activeQueueItemId: selectActiveQueueItemId(s),
    activeCollectionType: selectActiveCollectionType(s),
    activeCollectionId: selectActiveCollectionId(s),
    activeCollectionName: selectActiveCollectionName(s),
    status: selectPlaybackStatus(s),
    isPlaying: selectIsPlaying(s),
    playbackState: selectPlaybackStatus(s),
    isBuffering: selectIsBuffering(s),
    // progress removed - now in shared values (progressState.ts)
    // bufferedPosition removed — now in shared values (progressState.buffered)
    repeatMode: selectRepeatMode(s),
    shuffleOn: selectShuffleOn(s),
    canSkip: selectCanSkip(s),
    canSkipReason: selectCanSkipReason(s),
    isQueueEmpty: selectIsQueueEmpty(s),
    isQueueLoading: selectIsQueueLoading(s),
    isLoadingMore: selectIsLoadingMore(s),
    playbackLimitConfig: s.playbackLimitConfig,
    hasReachedPlaybackLimit: s.hasReachedPlaybackLimit,
    isAutoPlayEnabled: selectIsAutoPlayEnabled(s),
    foryouQueue: s.foryouQueue,
    playlistQueue: s.playlistQueue,
  };
};
